import { SancareBreadcrumbItem } from '@sancare/typescript-commons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { PmsiTypeEnum } from '@/store/modules/settings/types'

import { parseStayTrail, serializeStayTrail } from './stayTrail'

// TODO FT_CODAGE-308: déclarer la hiérarchie dans les route.meta plutôt qu'ici
const stayRouteByPmsi: Record<PmsiTypeEnum, 'mco-stay' | 'smr-stay'> = {
  [PmsiTypeEnum.MCO]: 'mco-stay',
  [PmsiTypeEnum.SMR]: 'smr-stay',
}

export function useBreadcrumb() {
  const route = useRoute()
  const { t } = useI18n()

  // pmsi
  const pmsiType = computed<PmsiTypeEnum>(() => {
    if (route.params.pmsiType) {
      return route.params.pmsiType as PmsiTypeEnum
    }
    return (route.path as string).includes('smr') ? PmsiTypeEnum.SMR : PmsiTypeEnum.MCO
  })

  // mode
  const modeLabel = computed(() => {
    if (route.query.mode) {
      return t(`tab.${route.query.mode}`)
    }
    return null
  })

  // staylist
  const staylistLabel = computed(() => route.query.staylistLabel ?? null)
  // TODO FT_CODAGE-309: virer le fallback sessionStorage, tout faire passer par l'URL
  const staylistRoute = computed(() => {
    if (!staylistLabel.value || ['staylist', 'automation-stay-list', 'automation-editor', 'automation-history'].includes(route.name as string)) {
      return null
    }
    const sessionPath = window.sessionStorage.getItem('stayListRoute')
    if (sessionPath) {
      return sessionPath
    }
    return null
  })

  // stay
  const stayLabel = computed(() => {
    if (route.query.stayLabel) {
      return `#${route.query.stayLabel}`
    }
    return null
  })
  const stayRoute = computed(() => {
    if (!stayLabel.value || !['rhs', 'maintenance-stay-history'].includes(route.name as string)) {
      return null
    }
    const routeQuery = { ...route.query }
    delete routeQuery.rhsNumber
    delete routeQuery.rhsId
    delete routeQuery.stayId
    if (route.name === 'maintenance-stay-history') {
      return {
        name: stayRouteByPmsi[pmsiType.value],
        params: {
          stayId: route.params.pmsiId,
          idType: pmsiType.value === PmsiTypeEnum.MCO ? 'rssid' : 'smrid'
        },
        query: routeQuery,
      }
    }
    if (route.name === 'rhs') {
      return {
        name: stayRouteByPmsi[pmsiType.value],
        params: {
          stayId: route.params.stayId,
          idType: 'id'
        },
        query: routeQuery,
      }
    }
    return null
  })

  // rhs
  const rhsLabel = computed(() => {
    if (route.query.rhsNumber) {
      return `RHS ${route.query.rhsNumber}`
    }
    return null
  })
  const rhsRoute = computed(() => {
    if (rhsLabel.value !== null && route.query.stayId && route.query.rhsId && route.name === 'maintenance-stay-history') {
      const routeQuery = { ...route.query }
      delete routeQuery.rhsNumber
      delete routeQuery.rhsId
      delete routeQuery.stayId
      return {
        name: 'rhs',
        params: {
          stayId: route.query.stayId,
          rhsId: route.query.rhsId,
        },
        query: routeQuery,
      }
    }
    return null
  })

  // recursive chain of stays reached through "previous stay" links (reconduction, chronic DAS)
  const stayTrailItems = computed((): SancareBreadcrumbItem[] => {
    const trail = parseStayTrail(route.query.stayTrail)

    return trail.map((iep, index) => {
      const routeQuery = { ...route.query }
      delete routeQuery.rumId
      delete routeQuery.externalRumId
      delete routeQuery.rhsNumber
      delete routeQuery.rhsId
      routeQuery.stayLabel = iep
      const ancestorsTrail = serializeStayTrail(trail.slice(0, index))
      if (ancestorsTrail) {
        routeQuery.stayTrail = ancestorsTrail
      } else {
        delete routeQuery.stayTrail
      }

      return {
        label: `#${iep}`,
        to: {
          name: stayRouteByPmsi[pmsiType.value],
          params: { idType: 'administrativelocalstayid', stayId: iep },
          query: routeQuery,
        },
      }
    })
  })

  // automation group editor / history
  const automationNewGroupLabel = computed(() => {
    if (route.name === 'automation-editor' && !route.query.groupId) {
      return t('breadcrumb.automationNew')
    }
    return null
  })
  const automationHistoryLabel = computed(() => {
    return route.name === 'automation-history' ? t('breadcrumb.history') : null
  })

  // history
  const contextLabel = computed(() => {
    if (stayLabel.value !== null && route.name === 'maintenance-stay-history') {
      return t('breadcrumb.history')
    }
    return null
  })

  const breadcrumbItems = computed((): SancareBreadcrumbItem[] => {
    const items: SancareBreadcrumbItem[] = []

    items.push({
      label: t('breadcrumb.home'),
      to: { name: 'home', params: { pmsiType: pmsiType.value } },
    })

    if (modeLabel.value) {
      items.push({
        label: modeLabel.value,
        to: { name: 'home', params: { pmsiType: pmsiType.value }, query: { mode: route.query.mode } },
      })
    }

    if (staylistLabel.value) {
      items.push({
        label: staylistLabel.value,
        to: staylistRoute.value,
      })
    }

    if (automationNewGroupLabel.value) {
      items.push({
        label: automationNewGroupLabel.value,
        to: null,
      })
    }

    if (automationHistoryLabel.value) {
      items.push({
        label: automationHistoryLabel.value,
        to: null,
      })
    }

    items.push(...stayTrailItems.value)

    if (stayLabel.value) {
      items.push({
        label: stayLabel.value,
        to: stayRoute.value,
      })
    }

    if (rhsLabel.value) {
      items.push({
        label: rhsLabel.value,
        to: rhsRoute.value,
      })
    }

    if (contextLabel.value) {
      items.push({
        label: contextLabel.value,
        to: null,
      })
    }

    return items
  })

  return { breadcrumbItems }
}
